import {
  ParsedBookingQuery,
  buildBookingLink,
  useParsedBookingQuery,
} from "@page-containers/booking/utils/bookingQuery";
import {useBookAndGoToConfirmation} from "@page-containers/booking/utils/useBookAndGoToConfirmation";
import {useFetchDuplicateAppointmentsLazy} from "@services/graphql/queries/appointment";
import {
  BookAppointmentRequest,
  RescheduleAppointmentRequest,
} from "@services/monolith/appointments";
import {useRouter} from "next/router";
import {useCallback, useMemo} from "react";

export function useBookIfUniqueAndGoToConfirmation(
  otherQueryParams: Partial<ParsedBookingQuery> = {},
  dropoutVisitorId?: string,
) {
  const query = useParsedBookingQuery();
  const patchedQuery = useMemo(() => ({...query, ...otherQueryParams}), [otherQueryParams, query]);

  const [fetchDuplicateAppointments] = useFetchDuplicateAppointmentsLazy();
  const bookAndGoToConfirmation = useBookAndGoToConfirmation(patchedQuery, dropoutVisitorId);
  const navigateToDoubleBookingGuard = useNavigateToDoubleBookingGuard(patchedQuery);
  return useCallback(
    async (
      input: BookAppointmentRequest | RescheduleAppointmentRequest,
      newQueryArgs: Partial<ParsedBookingQuery>,
    ) => {
      const {data} = await fetchDuplicateAppointments({
        variables: {
          input: {
            patientId: input.patientId,
            practiceId: input.practiceId,
            apptReasonId: input.apptReasonId,
            specialtyId: input.specialtyId,
            slotIds: input.slotIds,
          },
        },
      });

      const firstDuplicate = data?.duplicateAppointments?.duplicates?.[0];
      if (firstDuplicate) {
        await navigateToDoubleBookingGuard({
          ...newQueryArgs,
          doubleBookingOf: firstDuplicate.id,
          selectedSlotIds: input.slotIds,
        });
        return;
      }
      return bookAndGoToConfirmation(input, newQueryArgs);
    },
    [bookAndGoToConfirmation, fetchDuplicateAppointments, navigateToDoubleBookingGuard],
  );
}

function useNavigateToDoubleBookingGuard(query: Partial<ParsedBookingQuery>) {
  const router = useRouter();

  return useCallback(
    (moreQuery: Partial<ParsedBookingQuery>) => {
      return router.push(
        buildBookingLink({
          ...query,
          ...moreQuery,
          rescheduling: false,
          isSelectedTimeUnavailable: false,
        }),
        undefined,
        {shallow: true},
      );
    },
    [query, router],
  );
}
